import {
  Box,
  Container,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Stack,
  useMediaQuery,
  useTheme,
  IconButton,
  Drawer,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { psiTokens } from '../theme/psiTheme';
import { useLanguage } from '../contexts/LanguageContext';

const navItems = [
  { key: 'nav.home', to: '/' },
  { key: 'nav.services', to: '/services' },
  { key: 'nav.learnMore', to: '/learn-more' },
  { key: 'nav.about', to: '/about' },
  { key: 'nav.contact', to: '/contact' },
];

function LanguageToggle({ centered = false }) {
  const { language, setLanguage } = useLanguage();

  const optionSx = (code) => ({
    cursor: 'pointer',
    fontWeight: language === code ? 700 : 400,
    color: language === code ? psiTokens.primary.dark : psiTokens.primary.light,
  });

  return (
    <Box
      sx={{
        backgroundColor: '#f3f0f7',
        border: `1px solid ${psiTokens.primary.light}`,
        borderRadius: '14px',
        padding: '6px 12px',
        fontSize: '11px',
        display: 'flex',
        justifyContent: centered ? 'center' : 'flex-start',
        gap: '4px',
      }}
    >
      <Box component="span" onClick={() => setLanguage('en')} sx={optionSx('en')}>
        EN
      </Box>
      <Box component="span" sx={{ color: psiTokens.primary.light }}>
        |
      </Box>
      <Box component="span" onClick={() => setLanguage('es')} sx={optionSx('es')}>
        ES
      </Box>
    </Box>
  );
}

export default function Navbar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useLanguage();

  const NavMenu = () => (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      spacing={isMobile ? 0 : 3}
      sx={{ width: isMobile ? '100%' : 'auto' }}
    >
      {navItems.map((item) => (
        <Typography
          key={item.key}
          component={RouterLink}
          to={item.to}
          onClick={() => setMobileOpen(false)}
          sx={{
            fontSize: '13px',
            fontWeight: 600,
            color: psiTokens.text.primary,
            textDecoration: 'none',
            cursor: 'pointer',
            padding: isMobile ? '12px 16px' : '0',
            '&:hover': {
              color: psiTokens.primary.light,
            },
          }}
        >
          {t(item.key)}
        </Typography>
      ))}
    </Stack>
  );

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: psiTokens.background.primary,
        borderBottom: `1px solid ${psiTokens.border}`,
        height: '72px',
        display: 'flex',
        justifyContent: 'center',
      }}
      elevation={0}
    >
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{
            height: '72px',
            ...(isMobile
              ? { justifyContent: 'space-between' }
              : {
                  display: 'grid',
                  gridTemplateColumns: '1fr auto 1fr',
                  alignItems: 'center',
                }),
          }}
        >
          {/* Logo — pinned to the far left */}
          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              textDecoration: 'none',
              flexShrink: 0,
              justifySelf: isMobile ? 'auto' : 'start',
            }}
          >
            <Box
              component="img"
              src="/PSI - Editado.png"
              alt="PSI Logo"
              sx={{
                height: '48px',
                width: 'auto',
              }}
            />
          </Box>

          {/* Desktop Menu */}
          {!isMobile && (
            <>
              {/* Nav links — centered in the middle column regardless of
                  how wide the logo or the right-side controls are. */}
              <Box sx={{ justifySelf: 'center' }}>
                <NavMenu />
              </Box>

              {/* Language Toggle & CTA */}
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifySelf: 'end' }}>
                <LanguageToggle />

                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="contained"
                  sx={{
                    background: `linear-gradient(90deg, ${psiTokens.primary.dark}, ${psiTokens.primary.light})`,
                    color: '#ffffff',
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    padding: '8px 20px',
                    borderRadius: '10px',
                  }}
                >
                  {t('nav.getQuote')}
                </Button>
              </Stack>
            </>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <>
              <IconButton
                onClick={() => setMobileOpen(!mobileOpen)}
                sx={{ color: psiTokens.text.primary }}
              >
                <MenuIcon />
              </IconButton>

              <Drawer
                anchor="top"
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                sx={{
                  '& .MuiDrawer-paper': {
                    marginTop: '72px',
                  },
                }}
              >
                <Box sx={{ padding: 2 }}>
                  <NavMenu />
                  <Box sx={{ marginTop: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <LanguageToggle centered />
                    <Button
                      component={RouterLink}
                      to="/contact"
                      onClick={() => setMobileOpen(false)}
                      variant="contained"
                      fullWidth
                      sx={{
                        background: `linear-gradient(90deg, ${psiTokens.primary.dark}, ${psiTokens.primary.light})`,
                      }}
                    >
                      {t('nav.getQuote')}
                    </Button>
                  </Box>
                </Box>
              </Drawer>
            </>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
}
